import { useRef, useState, useEffect } from 'react';

/**
 * ScrollableTabBar — horizontal scroll container with fade edge hints.
 *
 * Wraps any horizontal nav/pill bar that can overflow on mobile.
 * Shows left/right gradient fades when content is scrollable.
 *
 * Props:
 *  - fadeFrom: CSS color for the gradient edges (default: var(--admin-bg-primary))
 *  - className: extra classes on the outer wrapper
 *  - innerClassName: classes on the scroll container (default includes scrollbar-hide)
 *  - mobileOnly: only show fades below md breakpoint (default: true)
 *  - children: the scrollable content (should include min-w-max or w-max)
 */
export default function ScrollableTabBar({
  fadeFrom = 'var(--admin-bg-primary)',
  className = '',
  innerClassName = '',
  mobileOnly = true,
  children,
}) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, []);

  const hideBreakpoint = mobileOnly ? 'sm:hidden' : '';

  return (
    <div className={`relative ${className}`}>
      {/* Left fade */}
      <div
        className={`pointer-events-none absolute left-0 top-0 bottom-0 w-6 z-10 transition-opacity duration-200 ${hideBreakpoint} ${canScrollLeft ? 'opacity-100' : 'opacity-0'}`}
        style={{ background: `linear-gradient(to right, ${fadeFrom}, transparent)` }}
      />
      {/* Right fade */}
      <div
        className={`pointer-events-none absolute right-0 top-0 bottom-0 w-6 z-10 transition-opacity duration-200 ${hideBreakpoint} ${canScrollRight ? 'opacity-100' : 'opacity-0'}`}
        style={{ background: `linear-gradient(to left, ${fadeFrom}, transparent)` }}
      />
      <div ref={scrollRef} className={`overflow-x-auto scrollbar-hide ${innerClassName}`}>
        {children}
      </div>
    </div>
  );
}
